import { Edges } from "@react-three/drei";
import * as THREE from "three";
import { useMemo } from "react";
import { fromMM } from "../Utils/frameUtils";
import {
  buildBaseFrameChannel,
  buildESPConeGeometry,
  buildHollowFrame,
} from "../Utils/geometryUtils";
import { useStore } from "../Store/zustandStore";

export default function BoxFan({ frame, selected }) {
  const [frameThickness] = useStore((state) => [fromMM(state.frameThickness)]);

  const frameLength = fromMM(frame.length);
  const frameHeight = fromMM(frame.height);
  const frameWidth = fromMM(frame.width);

  // BoxFan body dimensions
  const boxHeight = fromMM(frame.fan?.boxHeight ?? 900);
  const boxWidth = fromMM(frame.fan?.boxWidth ?? 1000);
  const boxLength = fromMM(frame.fan?.boxLength ?? 1000);
  const footSize = fromMM(100);

  // BOX-T-HT mounting bars, from the Fans table via frame.fan. The fan stands
  // on two steel bars that run ACROSS its width and stick out either side, so
  // the protruding ends can be bolted to the floor.
  //   mountingBarPitch       key A - bar to bar, ALONG the fan (world X)
  //   mountingBarHoleSpacing key B - hole to hole on one bar (world Z)
  //   mountingBarLength      key F - full length of each bar (world Z)
  // so hole spacing < bar length, both across the fan, while the pitch is the
  // one value measured along it.
  // Absent for any range with no mounting bars, in which case the four corner
  // feet are drawn as before.
  const mountingBarPitch       = fromMM(frame.fan?.mountingBarPitch ?? 0);
  const mountingBarHoleSpacing = fromMM(frame.fan?.mountingBarHoleSpacing ?? 0);
  const mountingBarLength      = fromMM(frame.fan?.mountingBarLength ?? 0);
  const hasBaseBar             = mountingBarPitch > 0 && mountingBarLength > 0;
  // Bar section and tab: not dimensioned in the selector, so these are drawn
  // to look right against the casing rather than claiming to be exact.
  const barHeight    = fromMM(70);   // selector key E - bar depth
  const barThickness = fromMM(80);   // bar width along the fan
  // Base frame: a perimeter channel that stands AROUND the mounting bars,
  // wrapping them from the side rather than sitting on top of them - the shape
  // in the client drawing. It spans the BOX width across the fan, sitting flush
  // under the casing, so the mounting bars are the only thing protruding either
  // side - they run the full extent (F) out to their floor-fixing bolt tabs.
  // Overall height of the channel, floor to box underside. Not yet dimensioned
  // in the selector, so it defaults to 70mm; the read from frame.fan is already
  // wired up for when the Fans table starts supplying it (see the commented-out
  // "Fan Baseframe" part lookup in MaicoUnit.cs, which resolves 50/75/100/125).
  const BASE_FRAME_HEIGHT_MM = 70;
  const baseFrameHeight = fromMM(frame.fan?.baseFrameHeight ?? BASE_FRAME_HEIGHT_MM);
  const channelThickness = fromMM(40);
  // The two ENDS are drawn in at the floor and splay outward as they rise, so
  // the bottom corners read as diagonals in the SIDE elevation - the angle
  // marked on the client drawing. The front elevation stays square. Both are
  // taken as a share of the frame height rather than fixed millimetres, so the
  // angle keeps its proportions if the height later comes from the database.
  const channelTaperHeight = baseFrameHeight / 3;
  const channelFootInset = channelTaperHeight * 0.875;
  // Channel spans the BOX width, not the bar length (key B), so the frame sits
  // flush under the casing and the mounting bars are the only thing sticking
  // out either side - they still run the full extent (F) to reach their bolt
  // tabs. Was the bar span (key B), which made the frame overhang the box.
  const baseFrameSpan = boxWidth;
  const holeRadius   = fromMM(12);
  // Fixing holes are dimensioned directly now: key A is hole-to-hole ON a bar,
  // so each hole sits half that either side of centre. Falls back to the middle
  // of the exposed stub when the table has no hole spacing.
  const holeZ = mountingBarHoleSpacing > 0
    ? mountingBarHoleSpacing / 2
    : (baseFrameSpan / 2 + mountingBarLength / 2) / 2;
  const cornerOffset = fromMM(20);
  // Connector openings, from the Fans table via frame.fan (see MaicoUnit.cs).
  // The inlet the cone lands on is a square (InletHeight); the discharge
  // connector is OutletHeight x OutletWidth. Both are centred on their face.
  const inletSize = fromMM(frame.fan?.boxInletHeight ?? 404);
  const outletHeight = fromMM(frame.fan?.boxOutletHeight ?? 404);
  const outletWidth = fromMM(frame.fan?.boxOutletWidth ?? 404);
  const grillBarThickness = fromMM(2);
  const cutoutGrillBarWidth = fromMM(100);
  const frontBackGrillSpacing = fromMM(150);
  const frontBackGrillBarWidth = fromMM(500);
  const frontBackGrillBarLength = fromMM(50);

  // Flexible connector between the cone and the box inlet: a flange, the
  // flexible sleeve itself, then a second flange - 50 + 100 + 50 = 200mm
  // overall. Not dimensioned in the selector, so the three lengths are fixed
  // here (the same way PRL-TC hardcodes its 75/100/75 stack in PRLTCFan.js).
  // It is always the square inlet cross-section, since it lands on the box's
  // inlet cutout at one end and the cone's narrow end at the other.
  const FLEX_FLANGE_MM = 50;
  const FLEX_SLEEVE_MM = 100;
  const flexFlangeLength = fromMM(FLEX_FLANGE_MM);
  const flexSleeveLength = fromMM(FLEX_SLEEVE_MM);
  const flexibleConnectorLength = fromMM(FLEX_FLANGE_MM * 2 + FLEX_SLEEVE_MM);

  // Outlet flange: the 50mm collar joining the AHU casing to the cone's wide
  // end, the mirror of the inlet flange at the other end of the unit. Length
  // is fixed at 50 (not selector-dimensioned), matching the 50mm flanges in
  // the flexible connector above.
  const OUTLET_FLANGE_MM = 50;
  const outletFlangeLength = fromMM(OUTLET_FLANGE_MM);

  // The exterior fan frame sits at the RIGHT end of the unit, so the AHU body
  // ends at this frame's left edge. The connection assembly runs rightward
  // from that wall as one continuous, gap-free chain:
  //   frame wall > outlet flange (50) > cone > flexible connector (200) >
  //   box cutout
  // coneGap is the length of that WHOLE chain, exactly as the client drawing
  // dimensions its 500 across the flange, cone and connector together - not
  // the cone alone. So both end pieces come out of the gap and the cone takes
  // what is left, keeping the box where the backend sized the frame for
  // (frame.length = coneGap + boxLength).
  const gap = fromMM(frame.fan?.coneGap ?? 500);
  const ahuLeftWallX = -frameLength / 2;
  const boxCutoutFaceX = ahuLeftWallX + gap;
  const boxCenterX = boxCutoutFaceX + boxLength / 2;
  // Fixed lengths, never scaled: the flange is 50 on the inlet and outlet
  // alike, as the client drawing shows. The shortest BOX-T-HT cone in the
  // ConeLengths table is 500 against 250 of end pieces, and the backend floors
  // ConeDistance at 500, so there is no case where these need to shrink.
  const outletFlangeSpan = outletFlangeLength;
  const flexConnectorLength = flexibleConnectorLength;
  const flexConnectorStartX = boxCutoutFaceX - flexConnectorLength;
  // Cone starts at the flange's DOWNSTREAM face, not at the frame wall, so it
  // lands on the flange the same way it lands on the connector at the far end.
  const coneStartX = ahuLeftWallX + outletFlangeSpan;
  // Vertical placement. The box does NOT hang off the frame centre - it stands
  // on the floor, on its mounting bars, on a base frame. Working up from the
  // ground (same reference PRLTCFan.js uses for its floor mounting frame):
  //
  //     ground  = -frameHeight/2 - ahuBaseFrameHeight (+frameThickness to
  //               cancel this group's own -frameThickness offset)
  //     mounting bars sit on the ground, barHeight tall
  //     base frame channel stands AROUND those bars, from the ground up to
  //       the box underside, so the bars are hidden inside it and only the
  //       bolt tabs poke out at each end
  //     box bottom    sits on the top of the channel
  //
  // The channel height IS the base frame height, so the box rides directly on
  // it. The bars are the same 70mm depth, so they now sit flush inside the
  // channel rather than being over-topped by it.
  //
  // This group is drawn at y = -frameThickness, so everything below is in
  // group-local coordinates (add frameThickness back).
  const ahuBaseFrameHeight = fromMM(frame.baseFrame?.height || 0);
  // The ground line must match where Frame.js actually puts the AHU base
  // frame's underside, or the fan floats. Frame.js centres that base frame at
  //     -(frameHeight/2 + frameThickness + baseFrameHeight/2) - 0.005
  // so its underside - the real floor - is one more half-height below:
  //     -frameHeight/2 - frameThickness - ahuBaseFrameHeight - 0.005
  // This group is itself drawn at y = -frameThickness, so add that back to get
  // the group-local figure. Deriving it this way rather than guessing the
  // stack keeps the fan on the floor whatever the frame thickness is; the old
  // form was short by frameThickness + 5mm and rose with thicker panels.
  // (the group's own -frameThickness offset cancels the one in that stack)
  const BASE_FRAME_GAP = 0.005; // Frame.js nudge, kept in step
  const groundY = -frameHeight / 2 - ahuBaseFrameHeight - BASE_FRAME_GAP;
  // Either way the box stands ON THE FLOOR - it is an exterior unit sitting on
  // the same ground as the AHU. With a base frame it rides on the channel;
  // without one it stands on its four corner feet. The old fallback put the
  // box at the frame centre (y = 0), which only looked right when boxHeight +
  // footSize happened to match the frame, and otherwise left it floating above
  // the floor or sunk through it.
  const boxCenterY = hasBaseBar
    ? groundY + baseFrameHeight + boxHeight / 2
    : groundY + footSize + boxHeight / 2;
  const boxCenterZ = 0;

  // TEMP DIAGNOSTIC - remove once the ground line is confirmed.
  // Frame.js puts the AHU base frame's UNDERSIDE at
  //   -(height/2 + frameThickness + baseFrameHeight/2) - 0.005 - baseFrameHeight/2
  // which is the true ground. Compare that against what this component uses.
  if (typeof window !== "undefined" && window.__BOXFAN_DEBUG) {
    const toMM = (v) => Math.round(v * 10000) / 10;
    const trueGroundWorld =
      -(frameHeight / 2 + frameThickness + ahuBaseFrameHeight / 2) -
      0.005 -
      ahuBaseFrameHeight / 2;
    const boxFanGroundWorld = groundY - frameThickness;
    /* eslint-disable no-console */
    console.log("[BoxFan]", frame.id, {
      frameHeight: toMM(frameHeight),
      frameThickness: toMM(frameThickness),
      ahuBaseFrameHeight: toMM(ahuBaseFrameHeight),
      baseFrameVisible: frame.baseFrame?.visible,
      hasBaseBar,
      mountingBarPitch: toMM(mountingBarPitch),
      mountingBarHoleSpacing: toMM(mountingBarHoleSpacing),
      mountingBarLength: toMM(mountingBarLength),
      baseFrameHeight: toMM(baseFrameHeight),
      boxHeight: toMM(boxHeight),
      groundY_local: toMM(groundY),
      boxFanGround_world: toMM(boxFanGroundWorld),
      trueGround_world: toMM(trueGroundWorld),
      ERROR_mm: toMM(boxFanGroundWorld - trueGroundWorld),
    });
    /* eslint-enable no-console */
  }

  // Inlet centre on the AHU-facing box face (in world-frame coords)
  const inletCenterY = boxCenterY;
  const inletCenterZ = boxCenterZ;

  // The cone runs from the AHU wall opening onto the flexible connector, which
  // in turn lands on the box cutout.
  //  - X: the wide end sits on the frame's left bound. AHUDesigner pulls this
  //    exterior frame back by one frame thickness (it draws no corner posts of
  //    its own), so that bound already coincides with the AHU body's right
  //    bound — no extra extension is needed, and the joint stays gap-free in
  //    both the geometry and the dimension chain. The narrow end stops short of
  //    the box by the connector's 200mm.
  //  - Y: this whole group is drawn at y = -frameThickness (so the box sits on
  //    the floor), which would drop the wide end below the opening — add
  //    frameThickness back on the wide end only, leaving the narrow end on the
  //    connector.
  const coneWideX = coneStartX;
  const coneNarrowX = flexConnectorStartX;
  const coneWideTopY = frameHeight / 2 + frameThickness;
  const coneWideBottomY = -frameHeight / 2 + frameThickness;

  // Outlet flange geometry: spans the wall to the cone's wide end, so the cone
  // starts ON the flange rather than on the bare frame. Sized to the full
  // frame opening (the cone's wide cross-section) and stood slightly proud, so
  // it reads as the collar it is.
  // Bore must equal the frame opening, not exceed it - see ESP.js. Sizing the
  // outer face to frame + proud and hollowing by a thin wall left a bore wider
  // than the cone, so the cone slid through and hid the flange.
  const outletFlangeProud = fromMM(15);
  const outletFlangeWallThickness = outletFlangeProud;
  // Centre of the flange's own span, carrying the same +frameThickness lift
  // the cone's wide end has (this group is drawn at y = -frameThickness), so
  // the two stay concentric.
  const outletFlangeX = ahuLeftWallX + outletFlangeSpan / 2;
  const outletFlangeY = (coneWideTopY + coneWideBottomY) / 2;
  const outletFlangeGeom = useMemo(
    () =>
      buildHollowFrame(
        outletFlangeSpan,
        frameHeight + outletFlangeProud * 2,
        frameWidth + outletFlangeProud * 2,
        outletFlangeWallThickness,
      ),
    [outletFlangeSpan, frameHeight, frameWidth, outletFlangeProud, outletFlangeWallThickness],
  );

  // Cone from the AHU frame opening (full frame cross-section, as PRLTCFan
  // does) down to the box's inlet face, landing exactly on both ends.
  // buildESPConeGeometry mirrors X (section[0].x *= -1, section[3].x *= -1)
  // before building, so pass pre-negated X — same trick as PRLTCFan's
  // leftPositionX — so the cone actually lands at coneWideX/coneNarrowX.
  const coneGeom = useMemo(() => {
    return buildESPConeGeometry([
      // top
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ - inletSize / 2),
        inletSize,
        0,
      ],
      // bottom
      [
        new THREE.Vector3(-coneWideX, coneWideBottomY, -frameWidth / 2),
        frameWidth,
        0,
        new THREE.Vector3(-coneNarrowX, inletCenterY - inletSize / 2, inletCenterZ - inletSize / 2),
        inletSize,
        0,
      ],
      // back
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, -frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ - inletSize / 2),
        0,
        -inletSize,
      ],
      // front
      [
        new THREE.Vector3(-coneWideX, coneWideTopY, frameWidth / 2),
        0,
        -frameHeight,
        new THREE.Vector3(-coneNarrowX, inletCenterY + inletSize / 2, inletCenterZ + inletSize / 2),
        0,
        -inletSize,
      ],
    ]);
  }, [coneWideX, coneNarrowX, coneWideTopY, coneWideBottomY, frameHeight, frameWidth, inletCenterY, inletCenterZ, inletSize]);

  // Flexible connector geometry: three hollow square tubes on the inlet
  // cross-section, butted end to end between the cone's narrow end and the box
  // inlet. The two flanges stand slightly proud of the sleeve so the joint
  // reads as the stepped lip it is on the drawing, matching the way the sleeve
  // is drawn narrower than its flanges on PRL-TC.
  //   buildHollowFrame(width, height, depth, thickness) extrudes along X, with
  //   height on Y and depth on Z, centred on the origin.
  const flexFlangeProud = fromMM(30);
  const flexWallThickness = fromMM(6);

  // Segment spans along X, working back from the box inlet face. The geometry
  // is built from these same spans, so the drawn tubes always match the
  // positions they are placed at.
  const flexFlangeSpan = flexFlangeLength;
  const flexSleeveSpan = flexSleeveLength;

  const flexFlangeGeom = useMemo(
    () =>
      buildHollowFrame(
        flexFlangeSpan,
        inletSize + flexFlangeProud,
        inletSize + flexFlangeProud,
        flexWallThickness,
      ),
    [flexFlangeSpan, inletSize, flexFlangeProud, flexWallThickness],
  );

  const flexSleeveGeom = useMemo(
    () => buildHollowFrame(flexSleeveSpan, inletSize, inletSize, flexWallThickness),
    [flexSleeveSpan, inletSize, flexWallThickness],
  );

  // Base frame channel. Only built for ranges that actually have a base frame;
  // the rest still fall back to the four corner feet.
  const baseFrameGeometry = useMemo(
    () =>
      hasBaseBar
        ? buildBaseFrameChannel(
            boxLength,
            baseFrameSpan,
            baseFrameHeight,
            channelThickness,
            channelFootInset,
            channelTaperHeight,
          )
        : null,
    [
      hasBaseBar,
      boxLength,
      baseFrameSpan,
      baseFrameHeight,
      channelThickness,
      channelFootInset,
      channelTaperHeight,
    ],
  );

  const flexOutletFlangeX = boxCutoutFaceX - flexFlangeSpan / 2;
  const flexSleeveX = boxCutoutFaceX - flexFlangeSpan - flexSleeveSpan / 2;
  const flexInletFlangeX = boxCutoutFaceX - flexFlangeSpan - flexSleeveSpan - flexFlangeSpan / 2;

  // End-face plate: the full box cross-section with the connector opening cut
  // out of the middle. Shape X maps to the box width (world Z) and shape Y to
  // the box height once the plate is rotated onto the face.
  const buildFaceShape = (holeWidth, holeHeight) => {
    const shape = new THREE.Shape();
    shape.moveTo(-boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, -boxHeight / 2);
    shape.lineTo(boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, boxHeight / 2);
    shape.lineTo(-boxWidth / 2, -boxHeight / 2);

    const hole = new THREE.Path();
    const halfW = holeWidth / 2;
    const halfH = holeHeight / 2;
    hole.moveTo(-halfW, -halfH);
    hole.lineTo(halfW, -halfH);
    hole.lineTo(halfW, halfH);
    hole.lineTo(-halfW, halfH);
    hole.lineTo(-halfW, -halfH);
    shape.holes.push(hole);
    return shape;
  };

  const inletShape = useMemo(
    () => buildFaceShape(inletSize, inletSize),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boxWidth, boxHeight, inletSize],
  );

  const outletShape = useMemo(
    () => buildFaceShape(outletWidth, outletHeight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [boxWidth, boxHeight, outletWidth, outletHeight],
  );

  // Box materials. BoxGeometry face order: [+X, -X, +Y, -Y, +Z, -Z]. -X is the
  // AHU-facing (inlet) side and +X the discharge side; both carry a connector
  // opening, so both are hidden and replaced by the cut-out plates below.
  const boxMaterials = useMemo(
    () => [
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide, transparent: true, opacity: 0 }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide, transparent: true, opacity: 0 }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
      new THREE.MeshStandardMaterial({ color: selected ? "Orange" : "DarkGrey", side: THREE.DoubleSide }),
    ],
    [selected],
  );

  const feetPositions = [
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [-boxLength / 2 + cornerOffset + footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, -boxWidth / 2 + cornerOffset + footSize / 2],
    [boxLength / 2 - cornerOffset - footSize / 2, -boxHeight / 2 - footSize / 2, boxWidth / 2 - cornerOffset - footSize / 2],
  ];

  // Grill bars across each connector opening, evenly spread over the opening's
  // height. The inlet and discharge are sized independently, so each face gets
  // its own ladder.
  const grillBarYs = (openingHeight) =>
    Array.from({ length: 5 }, (_, i) => -openingHeight / 2 + (i + 0.5) * (openingHeight / 5));

  const inletGrillBarPositions = grillBarYs(inletSize).map((y) => [-boxLength / 2 - 0.01, y, 0]);
  const outletGrillBarPositions = grillBarYs(outletHeight).map((y) => [boxLength / 2 + 0.01, y, 0]);

  const frontGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, -boxWidth / 2 + 0.01]);
  const backGrillBarPositions = Array.from({ length: 6 }, (_, i) => [0, (i - 2.5) * frontBackGrillSpacing, boxWidth / 2 - 0.01]);

  return (
    <group position={[0, -frameThickness, 0]}>
      {/* Outlet flange: 50mm collar joining the AHU casing to the cone. */}
      <mesh
        geometry={outletFlangeGeom}
        position={[outletFlangeX, outletFlangeY, 0]}
        userData={{ excludeProjections: ["left", "right"] }}
      >
        <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="Grey" widthTest={true} />
        </Edges>
      </mesh>

      <mesh geometry={coneGeom} userData={{ excludeProjections: ["left", "right"] }}>
        <meshStandardMaterial color={"DarkGrey"} side={THREE.DoubleSide} />
        <Edges scale={1} renderOrder={1000}>
          <meshBasicMaterial transparent color="DarkGrey" widthTest={true} />
        </Edges>
      </mesh>

      {/* Flexible connector joining the cone to the box inlet: flange (50),
          flexible sleeve (100), flange (50). Centred on the box inlet, which
          sits at the box's own height rather than the frame centre. */}
      {flexConnectorLength > 0 && (
        <>
          <mesh
            geometry={flexFlangeGeom}
            position={[flexInletFlangeX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>

          <mesh
            geometry={flexSleeveGeom}
            position={[flexSleeveX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DimGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>

          <mesh
            geometry={flexFlangeGeom}
            position={[flexOutletFlangeX, inletCenterY, inletCenterZ]}
            userData={{ excludeProjections: ["left", "right"] }}
          >
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        </>
      )}

      <group position={[boxCenterX, boxCenterY, boxCenterZ]}>
        <mesh material={boxMaterials}>
          <boxGeometry args={[boxLength, boxHeight, boxWidth]} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {/* Inlet (-X) connector: the square opening the cone lands on. */}
        <mesh position={[-boxLength / 2 - 0.01, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <extrudeGeometry args={[inletShape, { depth: grillBarThickness, bevelEnabled: false }]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {inletGrillBarPositions.map((p, i) => (
          <mesh key={`inlet-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[cutoutGrillBarWidth, inletSize, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {/* Discharge (+X) connector: OutletHeight x OutletWidth. The hole is
            centred on the plate's X, so mirroring the plate with a -90° Y
            rotation extrudes it inward from the far face without flipping the
            opening off-centre. */}
        <mesh position={[boxLength / 2 + 0.01, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <extrudeGeometry args={[outletShape, { depth: grillBarThickness, bevelEnabled: false }]} />
          <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
          <Edges scale={1} renderOrder={1000}>
            <meshBasicMaterial transparent color="Grey" widthTest={true} />
          </Edges>
        </mesh>

        {outletGrillBarPositions.map((p, i) => (
          <mesh key={`outlet-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[cutoutGrillBarWidth, outletWidth, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {frontGrillBarPositions.map((p, i) => (
          <mesh key={`front-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {backGrillBarPositions.map((p, i) => (
          <mesh key={`back-grill-${i}`} position={p} rotation={[Math.PI / 2, 0, 0]}>
            <boxGeometry args={[frontBackGrillBarWidth, frontBackGrillBarLength, grillBarThickness]} />
            <meshStandardMaterial color={selected ? "Orange" : "DarkGrey"} side={THREE.DoubleSide} />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))}

        {/* Base assembly, built up from the ground:
              two mounting bars run ACROSS the fan at pitch A, sitting ON the
                floor. Each is the full extent (F) with a fixing hole punched
                through the end that sticks out.
              the base frame is a perimeter CHANNEL standing around those bars
                - not a tray on top of them - spanning the BOX width so it
                sits flush under the casing and covers each bar from the side.
                Its two ends taper in at the floor, giving the angled bottom
                corners the client drawing shows in the SIDE elevation; the
                front elevation is square. The bars are the only thing that
                protrudes, running out to their bolt tabs either side.
              the box sits on top of the channel.
            Falls back to the four corner feet when a range has no base frame. */}
        {hasBaseBar ? (
          <group position={[0, groundY - boxCenterY, 0]}>
            {/* Mounting bars, on the ground */}
            {[-mountingBarPitch / 2, mountingBarPitch / 2].map((x, i) => (
              <group key={`baseframe-bar-${i}`} position={[x, barHeight / 2, 0]}>
                <mesh>
                  <boxGeometry args={[barThickness, barHeight, mountingBarLength]} />
                  <meshStandardMaterial color="DimGrey" metalness={0.6} roughness={0.5} />
                  <Edges scale={1} renderOrder={1000}>
                    <meshBasicMaterial transparent color="Grey" widthTest={true} />
                  </Edges>
                </mesh>
                {/* fixing hole near each end, in the part that sticks out */}
                {[-1, 1].map((s) => (
                  <mesh
                    key={`bar-hole-${s}`}
                    position={[0, 0, s * holeZ]}
                  >
                    <cylinderGeometry args={[holeRadius, holeRadius, barHeight * 1.2, 16]} />
                    <meshStandardMaterial color="Black" />
                  </mesh>
                ))}
              </group>
            ))}

            {/* Base frame channel, standing AROUND the bars and reaching from
                the ground to the box underside, so each bar is covered from
                the side and only its bolt tabs show. Geometry is built with
                its top face at y = 0, hence positioning by the box underside. */}
            <mesh
              position={[0, baseFrameHeight, 0]}
              geometry={baseFrameGeometry}
            >
              <meshStandardMaterial color="Silver" metalness={0.5} roughness={0.6} />
              <Edges scale={1} renderOrder={1000}>
                <meshBasicMaterial transparent color="Grey" widthTest={true} />
              </Edges>
            </mesh>
          </group>
        ) : (
          feetPositions.map((p, i) => (
          <mesh key={`foot-${i}`} position={p}>
            <boxGeometry args={[footSize, footSize, footSize]} />
            <meshStandardMaterial color="Black" />
            <Edges scale={1} renderOrder={1000}>
              <meshBasicMaterial transparent color="Grey" widthTest={true} />
            </Edges>
          </mesh>
        ))
        )}
      </group>
    </group>
  );
}
